/*=========================================================================================
  File Name: router.js
  Description: Routes for vue-router. Lazy loading is enabled.
  Object Strucutre:
                    path => router path
                    name => router name
                    component(lazy loading) => component to load
                    meta : {
                      rule => which user can have access (ACL)
                      breadcrumb => Add breadcrumb to specific page
                      pageTitle => Display title besides breadcrumb
                    }
  ----------------------------------------------------------------------------------------
  Item Name: Vuesax Admin - VueJS Dashboard Admin Template
  Author: Pixinvent
  Author URL: http://www.themeforest.net/user/pixinvent
==========================================================================================*/

import Vue from "vue";
import Router from "vue-router";
import { AuthenticationError } from "./api/errors";
import store from "./store/store";

Vue.use(Router);

const router = new Router({
    mode: "history",
    base: process.env.BASE_URL,
    scrollBehavior() {
        return { x: 0, y: 0 };
    },
    routes: [
        {
            // =============================================================================
            // MAIN LAYOUT ROUTES
            // =============================================================================
            path: "",
            component: () => import("./layouts/main/Main.vue"),
            children: [
                // =============================================================================
                // Theme Routes
                // =============================================================================
                {
                    path: "/",
                    name: "home",
                    component: () => import("./views/Home.vue")
                },
                {
                    path: "/searches",
                    name: "searches",
                    component: () => import("./views/pages/Searches.vue")
                },
                {
                    path: "/documents/:id",
                    name: "document-details",
                    component: () => import("./views/pages/DocumentDetails.vue")
                },
                {
                    path: "/manage-documents",
                    name: "manage-documents",
                    component: () => import("./views/pages/ManageDocuments.vue")
                },
                {
                    path: "/users",
                    name: "users",
                    component: () => import("./views/pages/Users.vue")
                },
                {
                    path:"/favorites",
                    name:"favorites",
                    component: () => import("./views/pages/FavoriteList.vue")
                },
                {
                    path:"/documents-favorite-lists/:id",
                    name:"document-favorite-lists",
                    component:() => import("./views/pages/DocumentsFavoriteLists.vue")
                },
                {
                    path:"/users_favorite_lists/:id",
                    name:"users_favorite_lists",
                    component:() => import("./views/pages/UsersFavoriteLists.vue")
                },
                {
                    path:"/levels/:id/:name",
                    name:"levels_list",
                    component:() => import("./views/pages/Levels.vue")
                },
                {
                    path:"/itens/:id/:name",
                    name:"levels_itens_list",
                    component:() => import("./views/pages/LevelsItens.vue")
                },
                {
                    path:"/results",
                    name:"results",
                    component:() => import("./views/pages/Result.vue")
                }
            ]
        },
        // =============================================================================
        // FULL PAGE LAYOUTS
        // =============================================================================
        {
            path: "",
            component: () => import("@/layouts/full-page/FullPage.vue"),
            children: [
                // =============================================================================
                // FULL PAGE LAYOUTS
                // =============================================================================
                {
                    path: "",
                    component: () => import("@/layouts/full-page/FullPage.vue"),
                    children: [
                        // =============================================================================
                        // PAGES
                        // =============================================================================
                        {
                            path: "/login",
                            name: "page-login",
                            component: () => import("@/views/pages/Login.vue")
                        },
                        {
                            path: "/pages/error-404",
                            name: "page-error-404",
                            component: () =>
                                import("@/views/pages/Error404.vue")
                        },
                        {
                            path: "/pages/error",
                            name: "page-error",
                            component: () => import("@/views/pages/Error.vue")
                        }
                    ]
                },
                // Redirect to 404 page, if no match found
                {
                    path: "*",
                    redirect: "/pages/error-404"
                }
            ]
        }
    ]
});

router.beforeEach(async (to, from, next) => {
    if (to.name === "page-login") {
        return next();
    }

    const { access_token: accessToken } = store.state.auth;

    if (!accessToken) {
        return next({ name: "page-login" });
    }

    try {
        await store.dispatch("auth/validateToken");
    } catch (error) {
        if (error instanceof AuthenticationError) {
            return next({ name: "page-login" });
        } else {
            return next({ name: "page-error" });
        }
    }

    return next();
});

router.afterEach(() => {
    // Remove initial loading
    const appLoading = document.getElementById("loading-bg");
    if (appLoading) {
        appLoading.style.display = "none";
    }
});

export default router;
